import React, { useEffect, useMemo, useReducer, useRef } from "react";

import { isEmpty, omit, without } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import { BillingDocumentAction } from "actions";

import { RecipientList } from "components/BillingWorkFlow/Send/RecipientList";
import { ReviewAndSend } from "components/BillingWorkFlow/Send/ReviewAndSend";
import { BottomSticky, Button, SecondaryButton } from "components/Form";
import { Column, Row } from "components/Layout";
import MessageBox from "components/MessageBox";
import { AbsolutePaper } from "components/Paper";

import { EMPTY_OBJECT } from "lib";

import { caseInsensitiveCompare } from "lib/compare";

import { selectEmailRecipientsByActiveBillingDocumentId } from "selectors";

import { useToggle } from "hooks";

const toggleRecipientReducer = (state, action) => {
  const {
    billingDocumentId,
    businessUser,
    emailRecipient,
    rawEmailAddress,
    add,
    remove,
  } = action;

  const existingDocumentState = state[billingDocumentId] || EMPTY_OBJECT;

  if (add || remove) {
    // Handle re-init / user selecting more documents to dispatch.
    const newState = {
      ...state,
      ...add,
    };
    return omit(newState, remove);
  }

  const businessUsers = businessUser
    ? (() => {
        const existing = existingDocumentState.businessUsers || [];
        const hasId = existing.some(bu => bu.id === businessUser.id);
        return hasId
          ? existing.filter(bu => bu.id !== businessUser.id)
          : [...existing, businessUser];
      })()
    : existingDocumentState.businessUsers;

  const emailRecipients = emailRecipient
    ? (() => {
        const existing = existingDocumentState.emailRecipients || [];
        const hasId = existing.some(er => er.id === emailRecipient.id);
        return hasId
          ? existing.filter(er => er.id !== emailRecipient.id)
          : [...existing, emailRecipient];
      })()
    : existingDocumentState.emailRecipients;

  return {
    ...state,
    [billingDocumentId]: {
      ...existingDocumentState,
      businessUsers,
      emailRecipients,
      rawEmailAddress: rawEmailAddress || existingDocumentState.rawEmailAddress,
    },
  };
};

export const SelectRecipients = ({ billingDocuments, onClose }) => {
  const emailRecipientsByBillingDocumentIdLookup = useSelector(
    selectEmailRecipientsByActiveBillingDocumentId,
  );

  const [toggleRecipientState, toggleRecipientAction] = useReducer(
    toggleRecipientReducer,
    billingDocuments.reduce((acc, row) => {
      const { id } = row.billingDocument;
      acc[id] = {
        businessUsers: [],
        emailRecipients: emailRecipientsByBillingDocumentIdLookup[id].filter(
          er => er.isAccountsRecipient,
        ),
      };
      return acc;
    }, {}),
  );

  const knownRecipientIdsByBillingDocumentIdRef = useRef(null);

  const sortKey = billingDocument =>
    `${billingDocument.type} ${billingDocument.number}`;
  const sortedBillingDocuments = useMemo(() => {
    return billingDocuments.sort((a, b) =>
      caseInsensitiveCompare(sortKey(a), sortKey(b)),
    );
  }, [billingDocuments]);

  useEffect(() => {
    // Add any new ones (documents with no state yet).
    const additions = billingDocuments.reduce((acc, { billingDocument }) => {
      if (isEmpty(toggleRecipientState[billingDocument.id])) {
        acc[billingDocument.id] = {
          businessUsers: [],
          emailRecipients: emailRecipientsByBillingDocumentIdLookup[
            billingDocument.id
          ].filter(er => er.isAccountsRecipient),
        };
      }
      return acc;
    }, {});

    // Build current lookup ids per billing document (only for current billingDocuments).
    const currentIdsByDocId = billingDocuments.reduce(
      (acc, { billingDocument }) => {
        const recipients =
          emailRecipientsByBillingDocumentIdLookup[billingDocument.id];
        acc[billingDocument.id] = new Set(recipients.map(er => er.id));
        return acc;
      },
      {},
    );

    const ref = knownRecipientIdsByBillingDocumentIdRef;
    const isFirstRun = ref.current === null;

    if (isFirstRun) {
      ref.current = currentIdsByDocId;
    } else {
      // For documents that already have state: find new recipients that are isAccountsRecipient and merge into additions.
      billingDocuments.forEach(({ billingDocument }) => {
        const { id } = billingDocument;
        const currentRecipients = emailRecipientsByBillingDocumentIdLookup[id];
        const previousIds = ref.current[id] || new Set();
        const newRecipients = currentRecipients.filter(
          er => !previousIds.has(er.id),
        );
        const newAccountsRecipients = newRecipients.filter(
          er => er.isAccountsRecipient,
        );

        if (newAccountsRecipients.length > 0) {
          const existing = toggleRecipientState[id] || EMPTY_OBJECT;
          additions[id] = {
            businessUsers: existing.businessUsers || [],
            emailRecipients: [
              ...(existing.emailRecipients || []),
              ...newAccountsRecipients,
            ],
            rawEmailAddress: existing.rawEmailAddress,
          };
        }
      });

      // Update ref to current lookup ids (only for current billingDocuments; removed docs drop out).
      ref.current = currentIdsByDocId;
    }

    // Remove any non-existent ones.
    const inValidKeys = without(
      Object.keys(toggleRecipientState),
      ...billingDocuments.map(({ billingDocument }) => billingDocument.id),
    );

    if (!isEmpty(inValidKeys) || !isEmpty(additions)) {
      toggleRecipientAction({ remove: inValidKeys, add: additions });
    }
  }, [
    billingDocuments,
    emailRecipientsByBillingDocumentIdLookup,
    toggleRecipientState,
  ]);

  const dispatch = useDispatch();

  const onSend = emailTemplateText => {
    const payload = Object.entries(toggleRecipientState)
      .map(([billingDocumentId, destinations]) => ({
        billing_document_id: billingDocumentId,
        email_recipients: destinations.emailRecipients.map(er => ({
          id: er.id,
          type: er.source.type,
        })),
        raw_email_address: destinations.rawEmailAddress,
        email_template_text: emailTemplateText,
        business_user_ids: destinations.businessUsers.map(bu => bu.id),
      }))
      .filter(
        ({ email_recipients, raw_email_address, business_user_ids }) =>
          email_recipients.length > 0 ||
          raw_email_address ||
          business_user_ids.length > 0,
      );
    dispatch(BillingDocumentAction.email(payload));
    onClose();
  };

  const [showConfirm, toggleShowConfirm] = useToggle(false);

  if (billingDocuments.length === 0) {
    return (
      <AbsolutePaper>
        <Column full>
          <MessageBox>Select documents to send</MessageBox>
        </Column>
      </AbsolutePaper>
    );
  }

  return (
    <AbsolutePaper>
      <form
        // Use a html form so we get email validation for free.
        onSubmit={e => {
          toggleShowConfirm();
          e.preventDefault();
        }}
      >
        <h1>Select Email Recipients</h1>

        {sortedBillingDocuments.map(({ billingDocument }) => {
          return (
            <RecipientList
              key={billingDocument.id}
              billingDocumentId={billingDocument.id}
              toggleRecipientState={
                toggleRecipientState[billingDocument.id] || EMPTY_OBJECT
              }
              toggleRecipientAction={toggleRecipientAction}
            />
          );
        })}

        <BottomSticky>
          <Row full background="white">
            <SecondaryButton type="button" onClick={onClose}>
              Cancel
            </SecondaryButton>
            <Button type="submit">Next Step (Review Message)</Button>
          </Row>
        </BottomSticky>
      </form>
      {showConfirm && (
        <ReviewAndSend onSend={onSend} onClose={toggleShowConfirm} />
      )}
    </AbsolutePaper>
  );
};
